import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Shopify always receives the integer number of boxes. The visible value can
 * instead be square metres (mq) or total pieces (pack), based on metafields.
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];

  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  unitType = 'unit';
  mqPerBox = 0;
  piecesPerBox = 0;

  connectedCallback() {
    super.connectedCallback();

    this.unitType = (this.dataset.tipoVendita || 'unit').trim().toLowerCase();
    this.mqPerBox = Number.parseFloat(this.dataset.mqPerBox || '0');
    this.piecesPerBox = Number.parseInt(this.dataset.piecesPerBox || '0', 10);

    const { minusButton, plusButton } = this.refs;
    if (minusButton.disabled) this.serverDisabledMinus = true;
    if (plusButton.disabled) this.serverDisabledPlus = true;

    this.initialized = true;
    this.updateButtonStates();
    this.updateCustomDisplay();
  }

  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return { canAdd: !wouldExceedMax, maxQuantity: max, cartQuantity, quantityToAdd };
  }

  getValue() {
    return this.refs.quantityInput.value;
  }

  setValue(value) {
    this.refs.quantityInput.value = value;
    this.updateCustomDisplay();
  }

  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = Number.parseInt(quantityInput.value, 10) || 0;

    quantityInput.min = min;
    if (max) quantityInput.max = max;
    else quantityInput.removeAttribute('max');
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();
    let newValue = currentValue;

    if ((currentValue - newMin) % newStep !== 0) {
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));
    if (newValue !== currentValue) quantityInput.value = newValue.toString();

    this.updateButtonStates();
    this.updateCustomDisplay();
  }

  getCurrentValues() {
    const { quantityInput } = this.refs;
    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    return Math.max(max - cartQuantity, min);
  }

  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) minusButton.disabled = value <= min;
    if (!this.serverDisabledPlus) plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
    this.updateCustomDisplay();
  }

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
    quantityInput.select();
  }

  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, Number.parseInt(event.target.value, 10) || 0));

    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
    this.updateCustomDisplay();
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = Number.parseInt(quantityInput.value, 10);
    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) quantityInput.value = clampedValue.toString();
    this.updateButtonStates();
    this.updateCustomDisplay();
  }

  updateCustomDisplay() {
    const quantity = this.getCurrentValues().value;
    const totalElement = this.querySelector('[ref="customTotal"]');
    const boxesElement = this.closest('.quantity-selector-wrapper')?.querySelector('[ref="customBoxes"]');

    if (this.unitType === 'mq' && Number.isFinite(this.mqPerBox) && this.mqPerBox > 0) {
      const totalMq = quantity * this.mqPerBox;
      const locale = document.documentElement.lang || 'it-IT';
      const formattedMq = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(totalMq);
      if (totalElement) totalElement.textContent = `${formattedMq} m²`;
    } else if (this.unitType === 'pack' && Number.isFinite(this.piecesPerBox) && this.piecesPerBox > 0) {
      const totalPieces = quantity * this.piecesPerBox;
      if (totalElement) totalElement.textContent = `${totalPieces} pz`;
    }

    if (boxesElement) {
      boxesElement.textContent = `${quantity} ${quantity === 1 ? 'confezione' : 'confezioni'}`;
    }
  }

  get quantityInput() {
    if (!this.refs.quantityInput) throw new Error('Missing <input ref="quantityInput" /> inside quantity selector');
    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
